import { Suspense, lazy, useEffect, useState } from 'react'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, KeyRound, MoreVertical, Pencil } from 'lucide-react'
import { toast } from '../../components/feedback/Toast.jsx'
import { api } from '../../lib/api.js'
import { useOnline } from '../../lib/useOnline.js'
import { fmtDateTime } from '../../lib/date.js'
import { primeKeyboard } from '../../lib/keyboard.js'
import BlockRenderer from '../../components/blocks/BlockRenderer.jsx'
import TapSecret from '../logins/TapSecret.jsx'
import { uploadFilesAsBlocks, droppedFiles } from './uploadBlocks.js'
import ClientCard from '../clients/ClientCard.jsx'
import ClientUpdates from '../clients/ClientUpdates.jsx'
import ClientForm from '../clients/ClientForm.jsx'

// BlockNote (and its whole chunk) loads only when someone actually edits.
// After a deploy, a client still running the previous build asks for a
// hashed chunk that no longer exists ("Failed to fetch dynamically imported
// module", Charles 2026-08-04) — one automatic reload picks up the new
// build and the tap works. The sessionStorage latch stops a reload loop if
// the chunk is genuinely broken.
const PageEditor = lazy(() =>
  import('../editor/PageEditor.jsx').then(
    (m) => {
      sessionStorage.removeItem('chunk-reloaded') // healthy again — re-arm
      return m
    },
    (err) => {
      if (!sessionStorage.getItem('chunk-reloaded')) {
        sessionStorage.setItem('chunk-reloaded', '1')
        window.location.reload()
        return new Promise(() => {}) // reloading — never settles
      }
      throw err
    }
  )
)

export default function PageDetailPage() {
  const { space, slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const [editing, setEditing] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [setupOpen, setSetupOpen] = useState(false)
  const online = useOnline()

  const { data, isLoading, error } = useQuery({
    queryKey: ['page', space, slug],
    queryFn: () => api(`/spaces/${space}/pages/${slug}`),
  })

  // A page created a moment ago opens straight into the editor.
  useEffect(() => {
    if (location.state?.edit) setEditing(true)
  }, [location.state])

  if (error) return <p className="text-sm text-danger">Page not found{online ? '' : ' in your synced copy'}.</p>
  // !data also covers a query paused offline — never destructure thin air.
  if (isLoading || !data) return <p className="text-sm text-muted">Loading…</p>

  const { page, breadcrumbs, children, credentials, client } = data
  // Offline is read-only: the synced copy can't take writes, so the edit
  // affordances step aside instead of offering a save that would fail.
  const canEdit = ['admin', 'editor'].includes(me.data?.user?.role) && online

  // Tick/untick a to-do straight from the read view — no editor session.
  // Saves through the same versioned PATCH the editor uses, so a revision
  // is kept and concurrent editors get the normal 409 conflict.
  async function toggleChecklistItem(ord) {
    const next = toggleNthChecklist(page.bodyJson, ord)
    const key = ['page', space, slug]
    qc.setQueryData(key, (d) => (d ? { ...d, page: { ...d.page, bodyJson: next } } : d))
    try {
      await api(`/pages/${page.id}`, {
        method: 'PATCH',
        body: { expectedVersion: page.version, bodyJson: next, note: 'checkbox' },
      })
    } catch (err) {
      toast(err.status === 409 ? 'Someone else just saved this page — reloading' : 'Couldn’t save', 'error')
    } finally {
      qc.invalidateQueries({ queryKey: key })
    }
  }

  // Drag files onto the page while READING — screenshots, PDFs, spreadsheets,
  // several at once — and they upload and append as blocks through the same
  // versioned PATCH as everything else (Charles, 2026-08-04). No editor
  // session needed; concurrent editors get the normal 409.
  async function onReadDrop(e) {
    const files = droppedFiles(e)
    if (files.length === 0) return
    e.preventDefault()
    toast(`Uploading ${files.length} file${files.length === 1 ? '' : 's'}…`)
    const blocks = await uploadFilesAsBlocks(page.id, files)
    if (blocks.length === 0) return
    try {
      await api(`/pages/${page.id}`, {
        method: 'PATCH',
        body: { expectedVersion: page.version, bodyJson: [...(page.bodyJson || []), ...blocks], note: 'files dropped' },
      })
      toast(`Added ${blocks.length} file${blocks.length === 1 ? '' : 's'}`)
    } catch (err) {
      toast(err.status === 409 ? 'Someone else just saved this page — reloading' : 'Couldn’t save', 'error')
    } finally {
      qc.invalidateQueries({ queryKey: ['page', space, slug] })
    }
  }

  // A client page is three things (Charles, 2026-09-29): the setup box
  // (its note included) changed only through Edit, the dated Updates, and
  // below them the client's page — a free area edited IN PLACE, where a tap
  // anywhere starts typing or pasting. Every other page hands the whole
  // screen to the editor.
  const editorEl = (
    <Suspense fallback={<p className="text-sm text-muted">Loading editor…</p>}>
      <PageEditor
        page={page}
        hideTitle={Boolean(client)}
        autoFocus={Boolean(client)}
        onDone={(saved) => {
          setEditing(false)
          if (saved) qc.invalidateQueries({ queryKey: ['page', space, slug] })
        }}
      />
    </Suspense>
  )
  if (editing && !client) return editorEl

  const openEditor = canEdit
    ? (e) => {
        if (e.target.closest('a, button, input, textarea, video, audio, summary')) return
        if (window.getSelection()?.toString()) return
        // The client page's editor opens with the cursor in it; on iPhone
        // the keyboard only comes up if something is focused inside this
        // very tap, before the editor has loaded.
        if (client) primeKeyboard()
        setEditing(true)
      }
    : undefined

  return (
    <article
      onDragOver={canEdit && !editing ? (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault() } : undefined}
      onDrop={canEdit && !editing ? onReadDrop : undefined}
    >
      {/* breadcrumbs */}
      <nav className="mb-2 flex flex-wrap items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted">
        <Link to={`/${space}`} className="hover:text-ink">{data.space.name}</Link>
        {breadcrumbs.map((c) => (
          <span key={c.slug} className="flex items-center gap-1">
            <ChevronRight size={12} />
            <Link to={`/${space}/${c.slug}`} className="hover:text-ink">{c.title}</Link>
          </span>
        ))}
      </nav>

      <div className="mb-1 flex items-start justify-between gap-3">
        <h1 className="text-3xl font-semibold leading-tight text-ink">{page.title}</h1>
        {canEdit && !editing && (
          <div className="relative flex shrink-0 items-center gap-1.5">
            {/* On a client page Edit opens the setup form — the name and
                the setup (Charles, 2026-09-26/29). The page body below it
                is edited by tapping it, like any other page. */}
            <button
              onClick={() => (client ? setSetupOpen(true) : setEditing(true))}
              className="tap flex items-center gap-1.5 rounded-xl border border-ink px-3.5 py-2 text-sm font-medium text-ink active:bg-grouped"
            >
              <Pencil size={14} /> Edit
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              title="Page actions"
              className="tap flex items-center justify-center rounded-xl border border-ink text-ink active:bg-grouped"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <PageMenu
                page={page}
                currentSpace={space}
                onClose={() => setMenuOpen(false)}
                onMoved={(spaceSlug, pageSlug) => {
                  qc.invalidateQueries({ queryKey: ['spaces'] })
                  qc.invalidateQueries({ queryKey: ['space'] })
                  // Re-parenting keeps the same URL — refetch, or the
                  // breadcrumbs keep showing the old home.
                  qc.invalidateQueries({ queryKey: ['page', spaceSlug, pageSlug] })
                  setMenuOpen(false)
                  navigate(`/${spaceSlug}/${pageSlug}`, { replace: true })
                }}
                onDeleted={() => {
                  qc.invalidateQueries({ queryKey: ['spaces'] })
                  qc.invalidateQueries({ queryKey: ['space', space] })
                  toast('Page deleted')
                  navigate(`/${space}`, { replace: true })
                }}
              />
            )}
          </div>
        )}
      </div>
      {/* masthead rule — the Ink signature under every page head */}
      <p className="mb-5 border-b border-hairline pb-3 text-xs text-faint">
        {page.updatedBy ? `Updated by ${page.updatedBy.name} · ` : ''}
        {fmtDateTime(page.updatedAt)}
      </p>

      {/* Typed client record — the setup answers above the fold */}
      {client && <ClientCard client={client} canEdit={canEdit && !editing} onEdit={() => setSetupOpen(true)} />}
      {client && setupOpen && <ClientForm initial={client} onClose={() => setSetupOpen(false)} />}

      {/* Logins used on this page — placement is the whole credential feature */}
      {credentials.length > 0 && (
        <div className="card mb-5 divide-y divide-hairline">
          {credentials.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-2.5">
              <KeyRound size={15} className="shrink-0 text-faint" />
              <div className="min-w-0 shrink">
                <span className="text-sm font-medium text-ink">{c.label}</span>
                {c.username && <span className="ml-2 text-xs text-muted">{c.username}</span>}
              </div>
              {/* dotted leader, rider-form style, label → value */}
              <span aria-hidden className="min-w-3 flex-1 self-center border-b-[1.5px] border-dotted border-faint" />
              <TapSecret credentialId={c.id} />
            </div>
          ))}
        </div>
      )}

      {/* Dated updates: the setup box links down to them, and where they
          disagree with it, they win. */}
      {client && <ClientUpdates client={client} canEdit={canEdit} pageKey={['page', space, slug]} />}

      {/* Click-to-edit, Notion-style: for editors, a click/tap on the body
          opens the editor. Links, secrets, checkboxes and media keep their
          own behavior; a click that ends a text selection never counts. The
          editor itself still loads lazily — reading stays light. */}
      {client ? (
        // The client's page: everything under the Updates. For editors it
        // reaches well down the screen, so the empty space itself is the
        // thing to tap; viewers see only what's been written.
        (editing || canEdit || hasBody(page.bodyJson)) && (
          <section className="border-t border-hairline pt-4">
            {editing ? (
              editorEl
            ) : (
              <div
                onClick={openEditor}
                onTouchStart={canEdit ? () => {} : undefined}
                className={canEdit ? 'min-h-[45vh] cursor-text pb-10' : undefined}
                title={canEdit ? 'Click to add or edit' : undefined}
              >
                {hasBody(page.bodyJson) ? (
                  <BlockRenderer blocks={page.bodyJson} onToggleCheck={canEdit ? toggleChecklistItem : undefined} />
                ) : (
                  <p className="text-[15px] text-faint">Tap anywhere here to add notes, photos or videos, or to paste something in.</p>
                )}
              </div>
            )}
          </section>
        )
      ) : (
        <div
          onClick={openEditor}
          // iOS Safari only synthesizes click events on elements it deems
          // interactive; an empty touch handler is the documented opt-in.
          // Without it, tap-to-edit works on desktop but dies on iPhone.
          onTouchStart={canEdit ? () => {} : undefined}
          className={canEdit ? 'cursor-text' : undefined}
          title={canEdit ? 'Click to edit' : undefined}
        >
          <BlockRenderer blocks={page.bodyJson} onToggleCheck={canEdit ? toggleChecklistItem : undefined} />
        </div>
      )}

      {children.length > 0 && (
        <section className="mt-8">
          <h2 className="section-header mb-2">Subpages</h2>
          <div className="card divide-y divide-hairline">
            {children.map((c) => (
              <Link key={c.id} to={`/${space}/${c.slug}`} className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{c.title}</span>
                <ChevronRight size={16} className="shrink-0 text-faint" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  )
}

/** True when a page body holds anything — an untouched page is one empty
 *  paragraph, which should read as empty. */
const inlineText = (c) => (Array.isArray(c) ? c.map((n) => n?.text ?? inlineText(n?.content)).join('') : '')
function hasBody(blocks) {
  return (blocks || []).some((b) => b.type !== 'paragraph' || inlineText(b.content).trim() || hasBody(b.children))
}

/**
 * Toggle the nth checklist item (depth-first order — MUST match the ctx
 * counter walk in BlockRenderer). Returns a new tree; never mutates the
 * query cache's copy in place.
 */
function toggleNthChecklist(blocks, target) {
  let seen = -1
  const walk = (arr) =>
    (arr || []).map((b) => {
      const nb = { ...b }
      if (b.type === 'checkListItem') {
        seen++
        if (seen === target) nb.props = { ...b.props, checked: !b.props?.checked }
      }
      if (b.children?.length) nb.children = walk(b.children)
      return nb
    })
  return walk(blocks)
}

/** The ⋯ menu: move a page to another category, or delete it (soft — every
 *  version stays in history, and an admin can restore from the database). */
function PageMenu({ page, currentSpace, onClose, onMoved, onDeleted }) {
  const spaces = useQuery({ queryKey: ['spaces'], queryFn: () => api('/spaces') })
  const [moving, setMoving] = useState(false) // false | 'space' | 'parent'
  const targets = (spaces.data?.spaces || []).filter((s) => s.slug !== currentSpace && s.kind !== 'logins')
  // Candidate parents: every other page in this category. The server
  // rejects a descendant pick, so no tree-walk is needed here.
  const spacePages = useQuery({
    queryKey: ['space', currentSpace],
    queryFn: () => api(`/spaces/${currentSpace}/pages`),
    enabled: moving === 'parent',
  })
  const parents = (spacePages.data?.pages || []).filter((p) => p.id !== page.id)

  async function moveTo(target) {
    try {
      const res = await api(`/pages/${page.id}`, {
        method: 'PATCH',
        body: { expectedVersion: page.version, spaceId: target.id },
      })
      toast(`Moved to ${target.name}`)
      onMoved(res.spaceSlug, res.page.slug)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
      onClose()
    }
  }

  async function moveUnder(parentId, label) {
    try {
      await api(`/pages/${page.id}`, {
        method: 'PATCH',
        body: { expectedVersion: page.version, parentId, note: 'moved' },
      })
      toast(parentId ? `Moved under “${label}”` : 'Moved to top level')
      onMoved(currentSpace, page.slug)
    } catch (err) {
      toast(err.data?.error || err.message, 'error')
      onClose()
    }
  }

  async function remove() {
    if (!window.confirm(`Delete “${page.title}”? It disappears from the app; history is kept.`)) return onClose()
    try {
      await api(`/pages/${page.id}`, { method: 'DELETE' })
      onDeleted()
    } catch (err) {
      toast(err.message, 'error')
      onClose()
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute right-0 top-11 z-40 w-56 overflow-hidden rounded-xl border border-ink bg-white shadow-[4px_4px_0_#E6E4DE]">
        {moving === false ? (
          <>
            <button onClick={() => setMoving('space')} className="tap flex w-full items-center px-4 py-2.5 text-sm text-ink active:bg-grouped">
              Move to category…
            </button>
            <button onClick={() => setMoving('parent')} className="tap flex w-full items-center px-4 py-2.5 text-sm text-ink active:bg-grouped">
              Move under page…
            </button>
            <button onClick={remove} className="tap flex w-full items-center px-4 py-2.5 text-sm text-danger active:bg-grouped">
              Delete page
            </button>
          </>
        ) : moving === 'space' ? (
          <div className="max-h-72 overflow-y-auto">
            <div className="section-header px-4 pb-1 pt-3">Move to</div>
            {targets.map((s) => (
              <button key={s.slug} onClick={() => moveTo(s)} className="tap flex w-full items-center gap-2 px-4 py-2.5 text-sm text-ink active:bg-grouped">
                <span className="w-5 text-center grayscale">{s.icon}</span> {s.name}
              </button>
            ))}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            <div className="section-header px-4 pb-1 pt-3">Move under</div>
            {page.parentId != null && (
              <button onClick={() => moveUnder(null)} className="tap flex w-full items-center px-4 py-2.5 text-sm text-ink active:bg-grouped">
                ↖ Top level
              </button>
            )}
            {parents.map((p) => (
              <button key={p.id} onClick={() => moveUnder(p.id, p.title)} className="tap flex w-full items-center px-4 py-2.5 text-left text-sm text-ink active:bg-grouped">
                {p.title}
              </button>
            ))}
            {spacePages.isLoading && <p className="px-4 py-2.5 text-sm text-muted">Loading…</p>}
          </div>
        )}
      </div>
    </>
  )
}
